import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Alert, Spinner } from "../components/ui";

interface Persona {
  id: string;
  name: string | null;
  email: string;
  blurb: string;
  submitted?: boolean;
  approved?: boolean;
  pending?: number;
}

interface Personas {
  seeded: boolean;
  coordinator: Persona | null;
  staff: Persona[];
  students: Persona[];
  industry: Persona[];
}

/**
 * Demo-only persona picker. Signs in as any seeded user without credentials,
 * so the whole flow can be walked through without Google or email.
 */
export default function Demo() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["demo", "personas"],
    queryFn: () => api.get<Personas>("/demo/personas"),
  });

  const signIn = useMutation({
    mutationFn: (userId: string) => api.post("/demo/login", { userId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.removeQueries({ queryKey: ["student"] });
      navigate("/");
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not sign in"),
  });

  if (isLoading) return <div className="p-12"><Spinner label="Loading personas" /></div>;

  return (
    <div className="min-h-screen bg-uoa-sand">
      <header className="bg-uoa-blue text-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <span aria-hidden className="grid h-9 w-9 place-items-center rounded bg-white font-bold text-uoa-blue">
              IS
            </span>
            <span className="text-lg font-semibold tracking-tight">Intern-Seek</span>
          </Link>
          <span className="rounded-full bg-amber-400 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-950">
            Demo mode
          </span>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-5xl px-6 py-10">
        <h1 className="text-3xl font-semibold text-heading">Sign in as anyone</h1>
        <p className="mt-2 max-w-2xl text-uoa-grey">
          Every account below is sample data. Choosing one signs you straight in &mdash; no password, no
          Google account, no email. Use it to walk the whole flow end to end.
        </p>

        {error && <div className="mt-6"><Alert tone="error">{error}</Alert></div>}

        {!data?.seeded && (
          <div className="mt-6">
            <Alert tone="error">
              No demo data found. Run <code className="font-mono">npm run db:demo --workspace=@intern-seek/api</code>{" "}
              and reload.
            </Alert>
          </div>
        )}

        <Group
          title="Students"
          caption="Submit an expression of interest, upload a CV, nominate a referee, apply for positions."
          personas={data?.students ?? []}
          badge={(p) => (p.submitted
            ? { text: "EOI submitted", tone: "bg-green-100 text-green-800" }
            : { text: "Not yet submitted", tone: "bg-gray-100 text-gray-700" })}
          onPick={(id) => signIn.mutate(id)}
          busy={signIn.isPending}
        />

        <Group
          title="Academic staff"
          caption="Respond to reference requests from students who nominated you."
          personas={data?.staff ?? []}
          badge={(p) => (p.pending
            ? { text: `${p.pending} request${p.pending === 1 ? "" : "s"}`, tone: "bg-amber-100 text-amber-800" }
            : null)}
          onPick={(id) => signIn.mutate(id)}
          busy={signIn.isPending}
        />

        <Group
          title="Coordinator"
          caption="An academic staff member with an extra Admin tab: approves organisations and positions, manages the staff directory."
          personas={data?.coordinator ? [data.coordinator] : []}
          badge={() => ({ text: "Elevated staff", tone: "bg-uoa-blue/10 text-heading" })}
          onPick={(id) => signIn.mutate(id)}
          busy={signIn.isPending}
        />

        <Group
          title="Employers"
          caption="Post internship positions and review applicants. Unapproved organisations see a restricted view."
          personas={data?.industry ?? []}
          badge={(p) => (p.approved
            ? { text: "Approved", tone: "bg-green-100 text-green-800" }
            : { text: "Awaiting approval", tone: "bg-amber-100 text-amber-800" })}
          onPick={(id) => signIn.mutate(id)}
          busy={signIn.isPending}
        />
      </main>
    </div>
  );
}

function Group({
  title, caption, personas, badge, onPick, busy,
}: {
  title: string;
  caption: string;
  personas: Persona[];
  badge: (p: Persona) => { text: string; tone: string } | null;
  onPick: (id: string) => void;
  busy: boolean;
}) {
  if (personas.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="text-lg font-semibold text-heading">{title}</h2>
      <p className="mb-4 text-sm text-uoa-grey">{caption}</p>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {personas.map((p) => {
          const tag = badge(p);
          return (
            <li key={p.id}>
              <button
                onClick={() => onPick(p.id)}
                disabled={busy}
                className="card flex h-full w-full flex-col items-start gap-1 text-left transition
                           hover:border-uoa-blue hover:shadow-md disabled:opacity-50"
              >
                <span className="font-semibold text-heading">{p.name ?? p.email}</span>
                {p.blurb && <span className="text-sm text-uoa-grey">{p.blurb}</span>}
                <span className="truncate text-xs text-uoa-grey">{p.email}</span>
                {tag && (
                  <span className={`mt-2 rounded-full px-2 py-0.5 text-xs font-medium ${tag.tone}`}>
                    {tag.text}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
